import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, ExternalLink, Eye, MailPlus, Paperclip, RotateCcw, Trash2, Upload, UserX } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Cartao, Selo, classesBotao, type CorSelo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { CampoData, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { TextoCortado } from '../../componentes/texto-cortado';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import type { PropsCartaoNaFicha } from '../../nucleo/modulos-web';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, email as emailFormatado, mesAno, nomeDeArquivo, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { discretoAtivo, nomesOcultos } from '../../nucleo/discreto';

/** Espelho das respostas de `modules/portal/src/equipe.ts`. */
export interface Acesso {
  id: string;
  conta_id: string;
  parceiro_id: string;
  parceiro_nome?: string;
  papel: string;
  areas: string[];
  estado: 'convidado' | 'ativo' | 'revogado';
  email: string;
  nome: string;
  anonimizada: boolean;
  /** Demanda 06/10: o e-mail é de alguém da equipe do tenant (marca "Equipe"). */
  equipe?: boolean;
  convidado_em: string;
  ultimo_acesso_em: string | null;
}

interface Entrega {
  id: string;
  area: string;
  competencia: string | null;
  titulo: string;
  publicado_em: string;
  visto_em: string | null;
  baixado_em: string | null;
  nome: string;
  publicado_por: string | null;
}

interface Envio {
  id: string;
  area: string;
  tipo: string;
  competencia: string | null;
  descricao: string;
  created_at: string;
  recebido_em: string | null;
  nome: string;
  enviado_por: string;
}

interface Passo {
  ator_tipo: string;
  acao: string;
  entidade: string;
  created_at: string;
  autor: string;
  ver_como: boolean;
}

const PAPEIS = ['dono', 'financeiro', 'dp', 'fiscal', 'societario', 'colaborador'] as const;
const AREAS = ['financeiro', 'dp', 'fiscal', 'contabil', 'societario', 'geral'] as const;
export const COR_ESTADO: Record<Acesso['estado'], CorSelo> = { convidado: 'laranja', ativo: 'verde', revogado: 'neutro' };

function Convidar({ parceiroId, contatos, aberto, aoFechar }: { parceiroId: string; contatos: PropsCartaoNaFicha['contatos']; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('portal');
  const tn = useTranslation().t;
  const qc = useQueryClient();
  const toast = useToast();
  const comEmail = contatos.filter((c) => c.email);
  const [contato, setContato] = useState<string>(comEmail[0]?.id ?? '');
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [papel, setPapel] = useState<string>('dono');
  const [idioma, setIdioma] = useState<string>('pt');
  const escolhido = comEmail.find((c) => c.id === contato);
  const enviar = useMutation({
    mutationFn: () =>
      post(`/portal/parceiros/${parceiroId}/acessos`, {
        email: escolhido ? escolhido.email : email,
        nome: escolhido ? escolhido.nome : nome,
        papel,
        idioma,
        ...(escolhido ? { contato_parceiro_id: escolhido.id } : {}),
      }),
    onSuccess: async () => {
      toast.sucesso(t('convite.enviado'));
      await qc.invalidateQueries({ queryKey: ['portal', 'acessos', parceiroId] });
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('convite.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviar.isPending} disabled={!escolhido && !email.includes('@')} onClick={() => enviar.mutate()}>
            {t('convite.enviar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-2">{t('convite.texto')}</p>
        {comEmail.length > 0 && (
          <Rotulo id="portal-contato" rotulo={t('convite.contato')}>
            <Selecao
              id="portal-contato"
              value={contato}
              onChange={(e) => setContato(e.target.value)}
              opcoes={[...comEmail.map((c) => ({ valor: c.id, rotulo: `${nomePessoa(c.nome)} (${emailFormatado(c.email)})` })), { valor: '', rotulo: t('convite.outro') }]}
            />
          </Rotulo>
        )}
        {!escolhido && (
          <>
            <Rotulo id="portal-email" rotulo={t('convite.email')}>
              <Entrada id="portal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Rotulo>
            <Rotulo id="portal-nome" rotulo={t('convite.nome')}>
              <Entrada id="portal-nome" value={nome} maxLength={120} onChange={(e) => setNome(e.target.value)} />
            </Rotulo>
          </>
        )}
        <Rotulo id="portal-papel" rotulo={t('convite.papel')} ajuda={t('convite.papelAjuda')}>
          <Selecao id="portal-papel" value={papel} onChange={(e) => setPapel(e.target.value)} opcoes={PAPEIS.map((p) => ({ valor: p, rotulo: t(`papel.${p}`) }))} />
        </Rotulo>
        <Rotulo id="portal-idioma" rotulo={t('convite.idioma')}>
          <Selecao
            id="portal-idioma"
            value={idioma}
            onChange={(e) => setIdioma(e.target.value)}
            opcoes={[
              { valor: 'pt', rotulo: 'Português' },
              { valor: 'en', rotulo: 'English' },
              { valor: 'es', rotulo: 'Español' },
            ]}
          />
        </Rotulo>
      </div>
    </Modal>
  );
}

/** Corpo do "Ver como o cliente": o modo discreto deste aparelho (e a opção de nomes) no momento em que abre. */
export function corpoVerComo() {
  return { discreto: { ativo: discretoAtivo(), nomes: nomesOcultos() } };
}

export function Publicar({ parceiroId, aberto, aoFechar }: { parceiroId: string; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('portal');
  const tn = useTranslation().t;
  const qc = useQueryClient();
  const toast = useToast();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [titulo, setTitulo] = useState('');
  const [area, setArea] = useState<string>('fiscal');
  const [competencia, setCompetencia] = useState('');
  const [descricao, setDescricao] = useState('');
  const seletor = useRef<HTMLInputElement>(null);
  const publicar = useMutation({
    mutationFn: () => {
      const f = new FormData();
      f.set('titulo', titulo.trim());
      f.set('area', area);
      if (/^\d{4}-\d{2}$/.test(competencia)) f.set('competencia', competencia);
      if (descricao.trim()) f.set('descricao', descricao.trim());
      f.set('arquivo', arquivo!, arquivo!.name);
      return post(`/portal/parceiros/${parceiroId}/entregas`, f);
    },
    onSuccess: async () => {
      toast.sucesso(t('publicar.publicado'));
      setArquivo(null);
      setTitulo('');
      setDescricao('');
      await qc.invalidateQueries({ queryKey: ['portal', 'entregas', parceiroId] });
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('publicar.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={publicar.isPending} disabled={!arquivo || !titulo.trim()} onClick={() => publicar.mutate()}>
            {t('publicar.enviar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-2">{t('publicar.texto')}</p>
        {/* Review r23 visual (M2): o seletor de arquivo do padrão do núcleo (botão traduzido e o input escondido), nunca o
            "Escolher arquivo / Nenhum arquivo escolhido" nativo no idioma do navegador. */}
        <div className="flex flex-col gap-1.5">
          <span id="portal-arquivo-rotulo" className="text-sm font-medium text-tinta-2">
            {t('publicar.arquivo')}
          </span>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Botao id="portal-arquivo" aria-describedby="portal-arquivo-rotulo" icone={<Paperclip className="size-4" aria-hidden />} onClick={() => seletor.current?.click()}>
              {arquivo ? t('publicar.trocarArquivo') : t('publicar.escolherArquivo')}
            </Botao>
            {arquivo && (
              <TextoCortado className="min-w-0 text-sm text-tinta-2" data-portal-arquivo-escolhido>
                {nomeDeArquivo(arquivo.name)}
              </TextoCortado>
            )}
            <input
              ref={seletor}
              type="file"
              className="sr-only"
              tabIndex={-1}
              aria-label={t('publicar.arquivo')}
              title={t('publicar.arquivo')}
              accept="application/pdf,image/*,.xml,.csv,.txt,.doc,.docx,.xls,.xlsx,.odt,.ods"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>
        <Rotulo id="portal-titulo" rotulo={t('publicar.tituloCampo')}>
          <EntradaSensivel id="portal-titulo" mascara={null} livre rotulo={t('publicar.tituloCampo')} value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} />
        </Rotulo>
        <Rotulo id="portal-area" rotulo={t('publicar.area')}>
          <Selecao id="portal-area" value={area} onChange={(e) => setArea(e.target.value)} opcoes={AREAS.map((a) => ({ valor: a, rotulo: t(`area.${a}`) }))} />
        </Rotulo>
        <Rotulo id="portal-competencia" rotulo={t('publicar.competencia')}>
          {/* Review r23 visual (M2): mês no idioma do app pelo `CampoData` (§6), também no Firefox e no Safari, que não têm o mês nativo. */}
          <CampoData id="portal-competencia" type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} />
        </Rotulo>
        <Rotulo id="portal-descricao" rotulo={t('publicar.descricao')}>
          <EntradaSensivel id="portal-descricao" mascara={null} livre rotulo={t('publicar.descricao')} value={descricao} maxLength={2000} onChange={(e) => setDescricao(e.target.value)} />
        </Rotulo>
      </div>
    </Modal>
  );
}

export function ListaAcessos({ parceiroId }: { parceiroId: string }) {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [revogar, setRevogar] = useState<Acesso | null>(null);
  const q = useQuery({ queryKey: ['portal', 'acessos', parceiroId], queryFn: () => get<{ itens: Acesso[] }>(`/portal/parceiros/${parceiroId}/acessos`) });
  const acao = useMutation({
    mutationFn: ({ id, qual }: { id: string; qual: 'reenviar' | 'revogar' | 'reativar' }) => post(`/portal/acessos/${id}/${qual}`, {}),
    onSuccess: async (_r, v) => {
      toast.sucesso(v.qual === 'revogar' ? t('estado.revogado') : t('convite.enviado'));
      setRevogar(null);
      await qc.invalidateQueries({ queryKey: ['portal', 'acessos', parceiroId] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const pode = s.pode('portal.acessos');
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  if (!q.data.itens.length) return <p className="text-sm text-tinta-3">{t('cartao.semAcessos')}</p>;
  return (
    <>
      <ul className="divide-y divide-borda" data-portal-acessos>
        {q.data.itens.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
            <div className="min-w-0 flex-1">
              <TextoCortado como="p" className="text-sm font-medium">
                {a.nome ? nomePessoa(a.nome) : emailFormatado(a.email)}
              </TextoCortado>
              <TextoCortado como="p" className="text-xs text-tinta-3">
                {`${emailFormatado(a.email)} · ${t(`papel.${a.papel}`)} · ${t('colunas.ultimo')}: ${a.ultimo_acesso_em ? dataHora(a.ultimo_acesso_em) : t('nunca')}`}
              </TextoCortado>
            </div>
            {a.equipe && <Selo cor="azul">{t('marcaEquipe')}</Selo>}
            <Selo cor={COR_ESTADO[a.estado]}>{t(`estado.${a.estado}`)}</Selo>
            {pode && a.estado === 'convidado' && (
              <Botao tamanho="sm" variante="fantasma" icone={<MailPlus className="size-4" aria-hidden />} carregando={acao.isPending && acao.variables?.id === a.id} onClick={() => acao.mutate({ id: a.id, qual: 'reenviar' })}>
                {t('acoes.reenviar')}
              </Botao>
            )}
            {pode && a.estado !== 'revogado' && (
              <Botao tamanho="sm" variante="perigo" icone={<UserX className="size-4" aria-hidden />} onClick={() => setRevogar(a)}>
                {t('acoes.revogar')}
              </Botao>
            )}
            {pode && a.estado === 'revogado' && !a.anonimizada && (
              <Botao tamanho="sm" variante="fantasma" icone={<RotateCcw className="size-4" aria-hidden />} onClick={() => acao.mutate({ id: a.id, qual: 'reativar' })}>
                {t('acoes.reativar')}
              </Botao>
            )}
          </li>
        ))}
      </ul>
      <Confirmar
        aberto={!!revogar}
        aoFechar={() => setRevogar(null)}
        aoConfirmar={() => revogar && acao.mutate({ id: revogar.id, qual: 'revogar' })}
        titulo={t('confirmar.revogarTitulo')}
        texto={t('confirmar.revogarTexto')}
        perigoso
        carregando={acao.isPending}
      />
    </>
  );
}

function ListaEntregas({ parceiroId }: { parceiroId: string }) {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [remover, setRemover] = useState<Entrega | null>(null);
  const q = useQuery({ queryKey: ['portal', 'entregas', parceiroId], queryFn: () => get<{ itens: Entrega[] }>(`/portal/parceiros/${parceiroId}/entregas`) });
  const tirar = useMutation({
    mutationFn: (id: string) => del(`/portal/entregas/${id}`),
    onSuccess: async () => {
      setRemover(null);
      await qc.invalidateQueries({ queryKey: ['portal', 'entregas', parceiroId] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  if (!q.data.itens.length) return <p className="text-sm text-tinta-3">{t('cartao.semEntregas')}</p>;
  return (
    <>
      <ul className="divide-y divide-borda" data-portal-entregas>
        {q.data.itens.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
            <div className="min-w-0 flex-1">
              <TextoCortado como="p" className="text-sm font-medium">
                {textoLivre(e.titulo)}
              </TextoCortado>
              <TextoCortado como="p" className="text-xs text-tinta-3">
                {`${t(`area.${e.area}`)}${e.competencia ? ` · ${mesAno(e.competencia.slice(0, 7))}` : ''} · ${t('colunas.publicado')} ${data(e.publicado_em)}${e.publicado_por ? ` · ${nomePessoa(e.publicado_por)}` : ''}`}
              </TextoCortado>
            </div>
            <Selo cor={e.baixado_em ? 'verde' : e.visto_em ? 'azul' : 'neutro'}>{e.baixado_em ? `${t('colunas.baixado')} ${data(e.baixado_em)}` : e.visto_em ? `${t('colunas.visto')} ${data(e.visto_em)}` : t('nunca')}</Selo>
            <a href={`/api/v1/portal/entregas/${e.id}/arquivo`} download className={classesBotao('fantasma', 'sm')} aria-label={`${t('acoes.baixar')}: ${textoLivre(e.titulo)}`}>
              <Download className="size-4" aria-hidden />
            </a>
            {s.pode('portal.publicar') && (
              <Botao tamanho="sm" variante="perigo" aria-label={`${t('acoes.remover')}: ${textoLivre(e.titulo)}`} icone={<Trash2 className="size-4" aria-hidden />} onClick={() => setRemover(e)} />
            )}
          </li>
        ))}
      </ul>
      <Confirmar
        aberto={!!remover}
        aoFechar={() => setRemover(null)}
        aoConfirmar={() => remover && tirar.mutate(remover.id)}
        titulo={t('confirmar.removerTitulo')}
        texto={t('confirmar.removerTexto')}
        perigoso
        carregando={tirar.isPending}
      />
    </>
  );
}

/**
 * Recebidos: o que o cliente enviou pelo portal. Spec r23 (M1): o nome do arquivo é escolhido pelo cliente e pode trazer CPF,
 * celular e nomes; no modo discreto ele passa pela regra dos anexos (`nomeDeArquivo`, com o nome do cliente e de quem
 * enviou), no texto e no `title`. Spec r23 (O6): a descrição que o cliente digitou aparece, com a censura de texto livre.
 */
export function ListaRecebidos({ parceiroId, nome }: { parceiroId: string; nome: string }) {
  const { t } = useTranslation('portal');
  const q = useQuery({ queryKey: ['portal', 'envios', parceiroId], queryFn: () => get<{ itens: Envio[] }>(`/portal/parceiros/${parceiroId}/envios`) });
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  if (!q.data.itens.length) return <p className="text-sm text-tinta-3">{t('cartao.semRecebidos')}</p>;
  return (
    <ul className="divide-y divide-borda" data-portal-recebidos>
      {q.data.itens.map((v) => (
        <li key={v.id} className="flex items-center gap-3 py-2">
          <div className="min-w-0 flex-1">
            <TextoCortado como="p" className="text-sm font-medium">
              {nomeDeArquivo(v.nome, nome, v.enviado_por)}
            </TextoCortado>
            {v.descricao && (
              <TextoCortado como="p" className="text-xs text-tinta-2" data-portal-recebido-descricao>
                {textoLivre(v.descricao, nome, v.enviado_por)}
              </TextoCortado>
            )}
            <TextoCortado como="p" className="text-xs text-tinta-3">
              {`${t(`tipoEnvio.${v.tipo}`)} · ${t(`area.${v.area}`)}${v.competencia ? ` · ${mesAno(v.competencia.slice(0, 7))}` : ''} · ${dataHora(v.created_at)} · ${nomePessoa(v.enviado_por)}`}
            </TextoCortado>
          </div>
          <a
            href={`/api/v1/portal/envios/${v.id}/arquivo`}
            download
            className={classesBotao('secundario', 'sm')}
            aria-label={`${t('acoes.baixar')}: ${nomeDeArquivo(v.nome, nome, v.enviado_por)}`}
          >
            <Download className="size-4" aria-hidden />
            {t('acoes.baixar')}
          </a>
        </li>
      ))}
    </ul>
  );
}

function Trilha({ parceiroId }: { parceiroId: string }) {
  const { t } = useTranslation('portal');
  const q = useQuery({ queryKey: ['portal', 'trilha', parceiroId], queryFn: () => get<{ itens: Passo[] }>(`/portal/parceiros/${parceiroId}/trilha`) });
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  if (!q.data.itens.length) return <p className="text-sm text-tinta-3">{t('cartao.semTrilha')}</p>;
  return (
    <ul className="divide-y divide-borda" data-portal-trilha>
      {q.data.itens.map((p, i) => (
        <li key={`${p.created_at}-${i}`} className="flex items-center gap-3 py-2 text-sm">
          <span className="numero w-36 shrink-0 text-xs text-tinta-3">{dataHora(p.created_at)}</span>
          <TextoCortado className="min-w-0 flex-1">
            {`${t(`trilha.acoes.${p.acao}`, { defaultValue: p.acao })}${['visualizou', 'baixou'].includes(p.acao) ? ` (${t(`trilha.entidades.${p.entidade}`, { defaultValue: p.entidade })})` : ''}`}
          </TextoCortado>
          <TextoCortado className="max-w-40 shrink-0 text-xs text-tinta-3">{p.ver_como ? t('trilha.equipe') : p.autor ? nomePessoa(p.autor) : p.ator_tipo === 'sistema' ? t('trilha.sistema') : t('trilha.cliente')}</TextoCortado>
        </li>
      ))}
    </ul>
  );
}

/** A aba Portal na ficha do cliente: acessos, documentos publicados, recebidos e a trilha, com "Ver como o cliente". */
export function CartaoPortal({ parceiroId, nome, contatos }: PropsCartaoNaFicha) {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const toast = useToast();
  const [aba, setAba] = useState<'acessos' | 'entregas' | 'recebidos' | 'trilha'>('acessos');
  const [convidar, setConvidar] = useState(false);
  const [publicar, setPublicar] = useState(false);
  const estado = useQuery({ queryKey: ['portal', 'estado'], queryFn: () => get<{ configurado: boolean; url: string | null }>('/portal/estado'), enabled: s.pode('portal.ver'), staleTime: 300_000 });
  const verComo = useMutation({
    // Review r23 visual (M4): o portal abre em outra origem, sem o modo discreto deste aparelho. O modo vai na criação da
    // sessão (corpo do POST, gravado no servidor), nunca na URL.
    mutationFn: () => post<{ url: string }>(`/portal/parceiros/${parceiroId}/ver-como`, corpoVerComo()),
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener,noreferrer'),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!s.pode('portal.ver')) return null;
  const configurado = estado.data?.configurado !== false;
  return (
    <Cartao
      titulo={t('cartao.titulo')}
      acao={
        <div className="flex flex-wrap justify-end gap-2">
          {s.pode('portal.ver_como_cliente') && configurado && (
            <Botao tamanho="sm" variante="fantasma" title={t('cartao.verComoAjuda')} icone={<Eye className="size-4" aria-hidden />} carregando={verComo.isPending} onClick={() => verComo.mutate()}>
              {t('cartao.verComo')}
            </Botao>
          )}
          {aba === 'entregas' && s.pode('portal.publicar') && (
            <Botao tamanho="sm" icone={<Upload className="size-4" aria-hidden />} onClick={() => setPublicar(true)}>
              {t('cartao.publicar')}
            </Botao>
          )}
          {aba === 'acessos' && s.pode('portal.acessos') && configurado && (
            <Botao tamanho="sm" icone={<MailPlus className="size-4" aria-hidden />} onClick={() => setConvidar(true)}>
              {t('cartao.convidar')}
            </Botao>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-cartao-portal>
        {!configurado && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('cartao.naoConfigurado')}</p>}
        {estado.data?.url && (
          <p className="text-xs text-tinta-3">
            {t('cartao.endereco')}:{' '}
            <a href={estado.data.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-acento-forte">
              {estado.data.url.replace(/^https?:\/\//, '')}
              <ExternalLink className="size-3" aria-hidden />
            </a>
          </p>
        )}
        <Abas
          idBase="portal-ficha"
          rotulo={t('cartao.titulo')}
          variante="compacto"
          ativa={aba}
          aoMudar={setAba}
          itens={[
            { chave: 'acessos', rotulo: t('cartao.acessos') },
            { chave: 'entregas', rotulo: t('cartao.entregas') },
            { chave: 'recebidos', rotulo: t('cartao.recebidos') },
            { chave: 'trilha', rotulo: t('cartao.trilha') },
          ]}
        />
        <PainelAba idBase="portal-ficha" chave={aba}>
          {aba === 'acessos' && <ListaAcessos parceiroId={parceiroId} />}
          {aba === 'entregas' && <ListaEntregas parceiroId={parceiroId} />}
          {aba === 'recebidos' && <ListaRecebidos parceiroId={parceiroId} nome={nome} />}
          {aba === 'trilha' && <Trilha parceiroId={parceiroId} />}
        </PainelAba>
      </div>
      <Convidar parceiroId={parceiroId} contatos={contatos} aberto={convidar} aoFechar={() => setConvidar(false)} />
      <Publicar parceiroId={parceiroId} aberto={publicar} aoFechar={() => setPublicar(false)} />
    </Cartao>
  );
}
